import React, { useState } from 'react'
import {
    Card,
    CardContent,
    Typography,
    Theme,
    makeStyles,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    Input,
    PopoverOrigin,
} from '@material-ui/core'
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'

import {
    ChatMessageContent,
    DeadlineResponse,
    WorkspaceResponse,
} from '../../../types'
import { useTheme } from '@material-ui/core/styles'
import { useLoriState } from '../../../../../../contexts/LoriContext'
import { useDeadline } from '../../../../../../hooks'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        borderLeft: '4px solid',
        borderBottom: '4px solid',
        borderRight: '1px solid',
        borderTop: '1px solid',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(1),
    },
    header: {
        display: 'flex',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        marginBottom: theme.spacing(2),
    },
    deadlineName: {
        fontWeight: 700,
        fontSize: '1.1rem',
    },
    description: {
        color: theme.palette.text.secondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '200px',
        marginLeft: theme.spacing(1),
    },
    formField: {
        marginBottom: theme.spacing(2),
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
    },
    submitButton: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    workspaceSection: {
        marginTop: theme.spacing(2),
    },
    formControl: {
        width: '100%',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    selectEmpty: {
        marginTop: theme.spacing(2),
    },
}))

// Add the MenuProps configuration from MultiWorkSpaceSelection
const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
    // This prevents the menu from closing after each selection
    getContentAnchorEl: null,
    // This ensures the menu stays in place
    anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
    } as PopoverOrigin,
    transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
    } as PopoverOrigin,
}

interface Props {
    message: ChatMessageContent
}

export const CreateDeadlineCard: React.FC<Props> = ({ message }) => {
    if (!message.function_call_response) return null
    const theme = useTheme()
    const { handleSendMessage, setShouldRefresh } = useLoriState()
    const { createDeadline } = useDeadline()

    const classes = useStyles(theme)

    // Extract deadline data from function_call_response if available
    const deadlineResponse = message.function_call_response as DeadlineResponse

    // Extract workspaces from function_call_response
    const workspaces: WorkspaceResponse[] =
        (message.function_call_response as any)?.deadline_workspaces ||
        deadlineResponse?.deadlineWorkspaces ||
        []

    const [deadlineName, setDeadlineName] = useState(
        deadlineResponse?.deadlineName || '',
    )
    const [deadlineDescription, setDeadlineDescription] = useState(
        deadlineResponse?.deadlineDescription || '',
    )
    const [deadlineDate, setDeadlineDate] = useState<Date | null>(
        deadlineResponse?.deadlineDate
            ? new Date(deadlineResponse.deadlineDate)
            : new Date(),
    )

    // Initialize selected workspaces from response if available
    const initialWorkspaceIds =
        deadlineResponse?.deadlineWorkspaces?.map((ws) => ws.id) || []
    const [selectedWorkspaces, setSelectedWorkspaces] = useState<number[]>(
        initialWorkspaceIds,
    )

    // Handle selection change
    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value as number[]
        setSelectedWorkspaces(value)
    }

    // Handle individual workspace click
    const handleWorkspaceClick = (
        event: React.MouseEvent<HTMLLIElement>,
        workspaceId: number,
    ) => {
        event.preventDefault() // Prevent default to avoid menu closing
        event.stopPropagation() // Stop propagation to avoid triggering other handlers

        let newSelectedWorkspaces: number[]

        if (selectedWorkspaces.includes(workspaceId)) {
            // If already selected, remove it
            newSelectedWorkspaces = selectedWorkspaces.filter(
                (id) => id !== workspaceId,
            )
        } else {
            // If not selected, add it
            newSelectedWorkspaces = [...selectedWorkspaces, workspaceId]
        }

        setSelectedWorkspaces(newSelectedWorkspaces)
    }

    const handleSubmit = () => {
        // Validate form
        if (!deadlineName.trim()) {
            alert('Please enter a deadline name')
            return
        }

        if (!deadlineDate) {
            alert('Please select a deadline date')
            return
        }

        if (selectedWorkspaces.length === 0) {
            alert('Please select at least one workspace')
            return
        }

        createDeadline({
            name: deadlineName,
            description: deadlineDescription,
            date: deadlineDate?.toISOString(),
            apartments: selectedWorkspaces,
        })
        setShouldRefresh(true)

        // Create a message with all the deadline information
        const deadlineInfo = `A deadline with the following properties was created:
        - Name: ${deadlineName}
        - Description: ${deadlineDescription}
        - Date: ${deadlineDate?.toLocaleDateString()}
        - Workspaces: ${selectedWorkspaces}`

        // Send the message to Lori
        handleSendMessage(deadlineInfo, 'system')
    }

    return (
        <Card className={classes.root}>
            <CardContent className={classes.content}>
                <div className={classes.header}>
                    <Typography className={classes.deadlineName}>
                        Create New Deadline
                    </Typography>
                </div>

                <TextField
                    className={classes.formField}
                    label="Deadline Name"
                    variant="outlined"
                    fullWidth
                    value={deadlineName}
                    onChange={(e) => setDeadlineName(e.target.value)}
                />

                <TextField
                    className={classes.formField}
                    label="Deadline Description"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={2}
                    value={deadlineDescription}
                    onChange={(e) => setDeadlineDescription(e.target.value)}
                />

                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DatePicker
                        className={classes.formField}
                        label="Deadline Date"
                        value={deadlineDate}
                        onChange={setDeadlineDate}
                        format="MM/dd/yyyy"
                        fullWidth
                        inputVariant="outlined"
                        disablePast
                    />
                </MuiPickersUtilsProvider>

                {/* Workspace selector integrated directly */}
                {workspaces && workspaces.length > 0 ? (
                    <FormControl className={classes.formControl}>
                        <InputLabel id="workspace-multiple-checkbox-label">
                            Select deadline workspaces
                        </InputLabel>
                        <Select
                            labelId="workspace-multiple-checkbox-label"
                            id="workspace-multiple-checkbox"
                            multiple
                            value={selectedWorkspaces}
                            onChange={handleChange}
                            input={<Input />}
                            renderValue={(selected) => {
                                const selectedIds = selected as number[]
                                if (selectedIds.length === workspaces.length) {
                                    return 'All workspaces selected'
                                }
                                return `${selectedIds.length} workspace${
                                    selectedIds.length !== 1 ? 's' : ''
                                } selected`
                            }}
                            MenuProps={MenuProps}
                        >
                            {workspaces.map((workspace) => (
                                <MenuItem
                                    key={workspace.id}
                                    value={workspace.id}
                                    onClick={(event) =>
                                        handleWorkspaceClick(
                                            event,
                                            workspace.id,
                                        )
                                    }
                                >
                                    <Checkbox
                                        checked={selectedWorkspaces.includes(
                                            workspace.id,
                                        )}
                                    />
                                    <ListItemText primary={workspace.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        No workspaces available
                    </Typography>
                )}

                <div className={classes.buttonContainer}>
                    <Button
                        variant="contained"
                        className={classes.submitButton}
                        onClick={handleSubmit}
                        disabled={
                            !deadlineName ||
                            !deadlineDate ||
                            selectedWorkspaces.length === 0
                        }
                    >
                        Create Deadline
                    </Button>
                </div>
            </CardContent>
        </Card>
    )
}
